import type { ResponseCookie } from 'next/dist/compiled/@edge-runtime/cookies';
import { InsforgeUser } from '@insforge/shared';

/**
 * Cookie names used by Insforge authentication
 * These are fixed and should not be changed to ensure consistency
 */
export const COOKIE_NAMES = {
  /** Session cookie storing the access token */
  SESSION: 'insforge-session',
  /** User info cookie storing basic user data for SSR */
  USER: 'insforge-user',
} as const;

/**
 * Default cookie configuration values
 */
export const COOKIE_DEFAULTS = {
  /** Default max age: 7 days in seconds */
  MAX_AGE: 7 * 24 * 60 * 60,
  /** Cookie path */
  PATH: '/',
  /** SameSite policy */
  SAME_SITE: 'lax' as const,
} as const;

/**
 * Options for cookie operations
 */
export interface CookieOptions {
  /** Max age in seconds (default: 7 days) */
  maxAge?: number;
  /** Whether to use secure cookies (HTTPS only). Auto-detected if not provided */
  secure?: boolean;
}

/**
 * Determines if secure cookies should be used based on environment
 * @param explicitSecure - Explicit secure value if provided
 * @returns Whether to use secure cookies
 */
export function isSecureCookie(explicitSecure?: boolean): boolean {
  if (explicitSecure !== undefined) {
    return explicitSecure;
  }
  // NODE_ENV is automatically set by Next.js runtime ('development' | 'production' | 'test')
  return process.env.NODE_ENV === 'production';
}

/**
 * Get base cookie options for auth cookies
 * @param options - Optional cookie configuration
 * @returns Cookie options object compatible with Next.js cookies API
 */
export function getBaseCookieOptions(
  options?: CookieOptions
): Omit<ResponseCookie, 'name' | 'value'> {
  const secure = isSecureCookie(options?.secure);
  const maxAge = options?.maxAge ?? COOKIE_DEFAULTS.MAX_AGE;

  return {
    httpOnly: true,
    secure,
    sameSite: COOKIE_DEFAULTS.SAME_SITE,
    maxAge,
    path: COOKIE_DEFAULTS.PATH,
  };
}

/**
 * Get cookie options for clearing/deleting a cookie
 * @param options - Optional cookie configuration
 * @returns Cookie options for deletion (maxAge: 0)
 */
export function getClearCookieOptions(
  options?: Pick<CookieOptions, 'secure'>
): Omit<ResponseCookie, 'name' | 'value'> {
  const secure = isSecureCookie(options?.secure);

  return {
    httpOnly: true,
    secure,
    sameSite: COOKIE_DEFAULTS.SAME_SITE,
    maxAge: 0,
    path: COOKIE_DEFAULTS.PATH,
  };
}

/**
 * Serialize user info for storage in cookie
 * @param user - User information to serialize
 * @returns JSON string of user info
 */
export function serializeUserCookie(user: InsforgeUser): string {
  return JSON.stringify({
    id: user.id,
    email: user.email,
    profile: user.profile || null,
  });
}

/**
 * Parse user info from cookie value
 * @param cookieValue - Raw cookie value
 * @returns Parsed user info or null if invalid
 */
export function parseUserCookie(cookieValue: string | undefined): InsforgeUser | null {
  if (!cookieValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(cookieValue) as unknown;

    // Type guard to validate the parsed data
    if (
      parsed &&
      typeof parsed === 'object' &&
      'id' in parsed &&
      'email' in parsed &&
      'profile' in parsed &&
      typeof parsed.id === 'string' &&
      typeof parsed.email === 'string' &&
      typeof parsed.profile === 'object'
    ) {
      return parsed as InsforgeUser;
    }

    return null;
  } catch {
    return null;
  }
}
